import React from 'react';
import { KeyRound, LockKeyhole } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const ACCESS_METHODS = [
  { value: 'public', label: 'Public URL — no sign-in' },
  { value: 'api_key', label: 'API key' },
  { value: 'bearer_token', label: 'Bearer access token' },
  { value: 'basic_auth', label: 'Username and password' },
  { value: 'custom_header', label: 'Custom header or PIN' },
];

const LinkedSourceAccessFields = ({
  authMethod = 'public', auth = {}, credentialsConfigured = false,
  onMethodChange, onAuthChange,
}) => {
  const setValue = (key, value) => onAuthChange({ ...auth, [key]: value });
  const privateMethod = authMethod !== 'public';

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-white p-3" data-testid="linked-source-access">
      <div className="flex items-center gap-2">
        {privateMethod ? <LockKeyhole size={14} className="text-indigo-600" /> : <KeyRound size={14} className="text-gray-500" />}
        <Label className="text-sm">Access method</Label>
      </div>
      <select
        value={authMethod}
        onChange={(event) => onMethodChange(event.target.value)}
        className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
        data-testid="linked-auth-method"
      >
        {ACCESS_METHODS.map(method => <option key={method.value} value={method.value}>{method.label}</option>)}
      </select>

      {privateMethod && credentialsConfigured && (
        <p className="rounded-md bg-emerald-50 px-2.5 py-2 text-xs text-emerald-800" data-testid="linked-saved-credential-hint">
          Encrypted credentials are already saved. Leave the fields empty to keep using them, or enter replacements below.
        </p>
      )}

      {authMethod === 'api_key' && (
        <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
          <div>
            <Label className="text-xs text-gray-600">Header name</Label>
            <Input value={auth.header_name || ''} onChange={event => setValue('header_name', event.target.value)} placeholder="X-API-Key" className="mt-1" data-testid="linked-auth-header-name" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">API key</Label>
            <Input type="password" value={auth.secret || ''} onChange={event => setValue('secret', event.target.value)} placeholder={credentialsConfigured ? 'Leave blank to keep saved key' : 'Paste API key'} autoComplete="new-password" className="mt-1" data-testid="linked-auth-secret" />
          </div>
        </div>
      )}

      {authMethod === 'bearer_token' && (
        <div>
          <Label className="text-xs text-gray-600">Access token</Label>
          <Input type="password" value={auth.token || ''} onChange={event => setValue('token', event.target.value)} placeholder={credentialsConfigured ? 'Leave blank to keep saved token' : 'Paste bearer token'} autoComplete="new-password" className="mt-1" data-testid="linked-auth-token" />
        </div>
      )}

      {authMethod === 'basic_auth' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-gray-600">Username</Label>
            <Input value={auth.username || ''} onChange={event => setValue('username', event.target.value)} placeholder={credentialsConfigured ? 'Leave blank to keep saved login' : 'Username'} autoComplete="off" className="mt-1" data-testid="linked-auth-username" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Password</Label>
            <Input type="password" value={auth.password || ''} onChange={event => setValue('password', event.target.value)} placeholder={credentialsConfigured ? 'Leave blank to keep saved login' : 'Password'} autoComplete="new-password" className="mt-1" data-testid="linked-auth-password" />
          </div>
        </div>
      )}

      {authMethod === 'custom_header' && (
        <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
          <div>
            <Label className="text-xs text-gray-600">Header name</Label>
            <Input value={auth.header_name || ''} onChange={event => setValue('header_name', event.target.value)} placeholder="X-Access-PIN" className="mt-1" data-testid="linked-auth-custom-header" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Header value or PIN</Label>
            <Input type="password" value={auth.secret || ''} onChange={event => setValue('secret', event.target.value)} placeholder={credentialsConfigured ? 'Leave blank to keep saved value' : 'Enter the value supplied by the website'} autoComplete="new-password" className="mt-1" data-testid="linked-auth-custom-secret" />
          </div>
        </div>
      )}

      {privateMethod && (
        <p className="text-[11px] leading-relaxed text-gray-500">
          Qflow encrypts this credential, restricts it to your organisation and this source, and never places it in extracted text or activity logs. Websites using interactive login, CAPTCHA or multi-factor authentication need a dedicated connector instead.
        </p>
      )}
    </div>
  );
};

export default LinkedSourceAccessFields;
